import { QueryClient } from "@tanstack/react-query";

/**
 * Shared QueryClient tuned for Moveload's workload.
 *
 * Goals under high load:
 * - Reduce duplicate concurrent requests (request deduplication).
 * - Keep reference data warm (vehicle catalog, platform switches, user roles).
 * - Avoid refetching on every mount/focus for stable reads.
 * - Keep errors retrying only on transient network failures, not 4xx.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2,        // 2 minutes default
      gcTime: 1000 * 60 * 10,          // 10 minutes cache retention
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      retry: (failureCount, error: any) => {
        // Retry only network-ish failures; don't hammer the DB on 4xx/5xx auth errors
        if (error?.status >= 400 && error?.status < 500) return false;
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      retry: false,
    },
  },
});

/**
 * Stale-time presets for different data categories.
 * Use these with useQuery({ staleTime: STALE. ... }) so the cache policy is explicit.
 */
export const STALE = {
  instant: 0,
  live: 1000 * 15,           // 15s — dashboards, tracking
  short: 1000 * 60,          // 1m — shipment lists
  medium: 1000 * 60 * 5,     // 5m — carrier profiles, availability
  long: 1000 * 60 * 15,      // 15m — reference catalogs, pricing constants
  static: 1000 * 60 * 60,    // 1h — vehicle catalog, platform switches
};
